/* assets/lab-unfold.js — the unfolded label (DIRECTION.md §4 "The unfolded label",
   §6 interactions 1, 2 and 4). Defines <lab-header>, the chrome's controller:

   - openers: every [data-lab-unfold="index|search"] on the page (the mark, Indice, Cerca,
     the phone strip's cells) records where focus should land; the popover itself is
     toggled natively through popovertarget, or by this script where popover is missing
     (the element becomes a <dialog> with the same id);
   - aria-expanded mirrored on every opener from the popover's toggle event (the Chiudi
     chip is not an opener and is skipped);
   - focus: the first destination (or the search field) on open, the opener on close;
   - Cmd/Ctrl+K opens with the search focused (never while a field or a modal dialog has
     the keystroke); Escape and light dismiss are native;
   - the index filter: typing hides [data-search] entries that do not contain the query,
     then their empty groups and bands; the kept predictive-search element appends its own
     results after its 300 ms debounce, untouched;
   - the claims line scrolls its current destination into view on phones, on load and
     again whenever a destination takes keyboard focus.

   Registered behind customElements.get; every listener is removed by an AbortController
   in disconnectedCallback. */

if (!customElements.get('lab-header')) {
  const SEARCH_INPUT = 'input[type="search"]';
  const FIRST_DESTINATION = '.unfold__dest a';
  const OPENERS = '[data-lab-unfold]';
  const POPOVER_OPENERS = '[popovertarget="lab-unfold"]:not([popovertargetaction="hide"])';
  const CLOSER = '[data-lab-close]';

  const normalise = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .toLowerCase()
      .trim();

  class LabHeader extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.unfold = document.getElementById('lab-unfold');
      this.supportsPopover = Object.prototype.hasOwnProperty.call(HTMLElement.prototype, 'popover') && CSS.supports('selector(:popover-open)');
      this.pendingFocus = 'index';
      this.opener = null;

      if (this.unfold) {
        if (!this.supportsPopover) this.unfold = this.toDialog(this.unfold);

        this.input = this.unfold.querySelector(SEARCH_INPUT);
        this.entries = Array.from(this.unfold.querySelectorAll('[data-search]'));
        this.groups = Array.from(this.unfold.querySelectorAll('[data-search-group]'));
        this.bands = Array.from(this.unfold.querySelectorAll('[data-search-band]'));

        document.addEventListener('click', (event) => this.onOpenerClick(event), { signal, capture: true });
        document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
        this.unfold.addEventListener('toggle', (event) => this.onToggle(event.newState === 'open'), { signal });
        this.unfold.addEventListener('click', (event) => this.onUnfoldClick(event), { signal });
        this.unfold.addEventListener('keydown', (event) => this.onUnfoldKeydown(event), { signal });
        window.addEventListener('scroll', () => { if (this.isOpen()) this.placeUnderChrome(); }, { signal, passive: true });
        window.addEventListener('resize', () => { if (this.isOpen()) this.placeUnderChrome(); }, { signal, passive: true });

        if (this.input) {
          this.input.addEventListener('input', () => this.filter(), { signal });
          this.input.form?.addEventListener('reset', () => queueMicrotask(() => this.filter()), { signal });
          if (this.input.value) this.filter();
        }

        // the privacy-choices entry reopens the consent banner when its script is present
        this.unfold.addEventListener(
          'click',
          (event) => {
            const link = event.target.closest('[data-privacy-choices]');
            if (!link || typeof window.showCookiePreferences !== 'function') return;
            event.preventDefault();
            this.close();
            window.showCookiePreferences();
          },
          { signal }
        );
      }

      this.revealCurrentDestination();
      window.addEventListener('resize', () => this.revealCurrentDestination(), { signal, passive: true });
      this.addEventListener('focusin', (event) => this.revealFocusedDestination(event), { signal });
    }

    disconnectedCallback() {
      this.controller.abort();
    }

    /* ---------- openers ---------- */

    onOpenerClick(event) {
      const opener = event.target.closest(OPENERS);
      if (!opener) return;
      this.pendingFocus = opener.dataset.labUnfold === 'search' ? 'search' : 'index';
      this.opener = opener;
      this.markEntry();
      if (this.supportsPopover) {
        // the mark and the words carry popovertarget: the browser toggles. An opener
        // without it (a plain button elsewhere) toggles here.
        if (!opener.hasAttribute('popovertarget')) {
          event.preventDefault();
          this.toggle();
        } else if (this.isOpen() && opener.dataset.labUnfold === 'search' && !this.unfold.contains(opener)) {
          // already open, asked for the search: keep it open and move focus
          event.preventDefault();
          this.focusTarget();
        }
        return;
      }
      event.preventDefault();
      this.toggle();
    }

    /* Cmd/Ctrl+K opens the label with the search focused. It must not fire while the
       visitor is writing (on macOS Ctrl+K is kill-to-end-of-line inside a field: the cart
       note, the contact message and the back-in-stock address all rely on it), and it must
       not open a popover behind an inert page while a modal dialog holds the top layer. */
    onKeydown(event) {
      const key = typeof event.key === 'string' ? event.key.toLowerCase() : '';
      if (key !== 'k' || !(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
      const target = event.target;
      if (target instanceof Element && target.closest('input, textarea, select, [contenteditable]')) return;
      if (this.modalIsOpen()) return;
      event.preventDefault();
      this.pendingFocus = 'search';
      this.opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.markEntry();
      if (this.isOpen()) {
        this.focusTarget();
      } else {
        this.open();
      }
    }

    /* a modal dialog (the cart sheet, the media dialog, and this label itself where the
       popover API is missing) owns the top layer and makes the page inert: opening a
       popover behind it would put the index out of reach. A dialog that is merely open is
       not modal and does not block anything. */
    modalIsOpen() {
      try {
        return Array.from(document.querySelectorAll('dialog[open]')).some((dialog) => dialog !== this.unfold && dialog.matches(':modal'));
      } catch (selectorError) {
        // no :modal support (older engines): the shortcut stays available
        return false;
      }
    }

    /* which word opened the label: the cream search zone leads when it was Cerca, so the
       value field is the first thing under the chrome at every width (the CSS reorders the
       three bands on [data-entry="search"]). Set before the popover opens, so the panel is
       never laid out twice. */
    markEntry() {
      if (this.unfold) this.unfold.dataset.entry = this.pendingFocus;
    }

    onUnfoldKeydown(event) {
      if (event.key !== 'Escape') return;
      // Escape closes the label at once. A search field with a value would otherwise clear
      // itself first and cancel the popover's own close request, so the visitor would need
      // a second press; the typed term is kept for the next opening.
      event.preventDefault();
      this.close();
    }

    /* the label drops from beneath the chrome wherever the chrome is (the mark zone and the
       shipping line sit above it until the page scrolls) */
    placeUnderChrome() {
      const chrome = this.closest('.lab-header-section') || this;
      const bottom = Math.max(0, Math.round(chrome.getBoundingClientRect().bottom));
      this.unfold.style.setProperty('--lab-chrome-bottom', `${bottom}px`);
    }

    onUnfoldClick(event) {
      // Chiudi. With the popover API the browser would hide the panel anyway
      // (popovertargetaction="hide"); this is what closes it in the <dialog> fallback,
      // where popovertarget means nothing.
      if (event.target.closest(CLOSER)) {
        this.close();
        return;
      }
      const link = event.target.closest('a[href]');
      if (!link || link.closest('[data-predictive-search]')) return;
      if (link.getAttribute('href')?.startsWith('#')) return;
      // choosing a destination closes the label before the navigation starts, so the
      // cross-document transition captures the page without the overlay
      this.close();
    }

    /* ---------- state ---------- */

    isOpen() {
      if (!this.unfold) return false;
      return this.supportsPopover ? this.unfold.matches(':popover-open') : this.unfold.open === true;
    }

    open() {
      if (!this.unfold || this.isOpen()) return;
      this.placeUnderChrome();
      if (this.supportsPopover) {
        this.unfold.showPopover();
      } else {
        this.unfold.showModal();
        this.onToggle(true);
      }
    }

    close() {
      if (!this.unfold || !this.isOpen()) return;
      if (this.supportsPopover) {
        this.unfold.hidePopover();
      } else {
        this.unfold.close();
      }
    }

    toggle() {
      if (this.isOpen()) {
        this.close();
      } else {
        this.open();
      }
    }

    onToggle(open) {
      // the Chiudi chip also carries popovertarget, but it hides rather than opens: it is
      // not an opener and must never advertise aria-expanded
      document.querySelectorAll(`${OPENERS}, ${POPOVER_OPENERS}`).forEach((opener) => {
        if (opener.hasAttribute('data-lab-close')) return;
        opener.setAttribute('aria-expanded', String(open));
      });
      if (open) {
        this.placeUnderChrome();
        this.focusTarget();
        return;
      }
      const opener = this.opener;
      this.opener = null;
      if (opener && opener.isConnected && typeof opener.focus === 'function') {
        opener.focus({ preventScroll: true });
      }
    }

    focusTarget() {
      const wantsSearch = this.pendingFocus === 'search';
      const target = wantsSearch ? this.input : this.unfold.querySelector(FIRST_DESTINATION);
      if (!target) return;
      requestAnimationFrame(() => {
        target.focus({ preventScroll: true });
        // asking for the search twice in a row replaces the term instead of appending to it
        if (wantsSearch && target instanceof HTMLInputElement) target.select();
      });
    }

    /* ---------- no popover: the same element as a modal dialog ---------- */

    toDialog(div) {
      const dialog = document.createElement('dialog');
      for (const { name, value } of Array.from(div.attributes)) {
        if (name !== 'popover') dialog.setAttribute(name, value);
      }
      dialog.append(...Array.from(div.childNodes));
      div.replaceWith(dialog);
      const { signal } = this.controller;
      dialog.addEventListener(
        'click',
        (event) => {
          if (event.target === dialog) dialog.close();
        },
        { signal }
      );
      dialog.addEventListener('close', () => this.onToggle(false), { signal });
      return dialog;
    }

    /* ---------- interaction 2: the index filter ---------- */

    filter() {
      const query = normalise(this.input?.value);
      for (const entry of this.entries) {
        entry.hidden = query.length > 0 && !normalise(entry.dataset.search).includes(query);
      }
      for (const group of this.groups) {
        group.hidden = query.length > 0 && !group.querySelector('[data-search]:not([hidden])');
      }
      for (const band of this.bands) {
        band.hidden = query.length > 0 && !band.querySelector('[data-search]:not([hidden])');
      }
    }

    /* ---------- the claims line on phones: the current word in view ----------

       Only when the word is not already whole on the rail. Aligning it to the start
       unconditionally looks right on paper — scroll-snap-align: start wants it there —
       but the browser clamps the value, so on a page whose word is the second or third
       one (Il frantoio, Vieni a trovarci) an unconditional assignment still moves the
       rail by the whole scroll range (40 px at 360 in Italian) for no reason, and that
       range is enough to cut the first word down to a 3 px sliver without bringing
       anything new into view. The rail therefore starts where the reader left it — at 0 —
       unless the current word really does hang off an edge, in which case it goes to the
       start of the rail and the clamp lands the last word inside the end margin the list
       padding buys it (lab-section-header.css: the bleed comment). Measured with
       getBoundingClientRect, not offsetLeft, so the nav's own padding and offsetParent
       do not have to be reasoned about, and matched with revealFocusedDestination below. */

    revealCurrentDestination() {
      const nav = this.querySelector('.lab-line__nav');
      const list = nav?.querySelector('.lab-line__list');
      const current = nav?.querySelector('[aria-current]');
      if (!nav || !list || !current) return;
      /* measure the rail as the stylesheet draws it, never as the last reveal left it */
      list.style.setProperty('--rail-end-slack', '0px');
      if (nav.scrollWidth <= nav.clientWidth) return;
      const rail = nav.getBoundingClientRect();
      const word = current.getBoundingClientRect();
      /* a pixel of tolerance: the rail's ends land on fractional pixels at some widths */
      const whole = word.left >= rail.left - 1 && word.right <= rail.right + 1;
      if (!whole) nav.scrollLeft += word.left - rail.left;
      /* the rest of this runs for an already-scrolled rail too: a resize that keeps the
         rail at its end (430 → 360 and back) re-measures from a scrollLeft the reader
         never set, and that position has the same stranded fragment as a fresh reveal */
      if (nav.scrollLeft <= 0 || nav.scrollLeft < nav.scrollWidth - nav.clientWidth - 1) return;
      /* Clamped at the end, so the rail stops wherever the tail runs out and that is
         rarely a word boundary: at 360 in Italian it strands 3 px of the first word at
         the margin, which reads as a rendering fault, not as "there is more to the
         left". Lend the tail exactly the stranded width — an item's box includes its
         leading bullet, so the rail then starts on "• IL FRANTOIO" — and never more than
         the margin itself: past that the strand is half a word, which is the same
         affordance the bled last word gives at the other end and wants no repair. */
      const fragment = [...list.children]
        .map((item) => item.getBoundingClientRect())
        .find((box) => box.left < rail.left - 1 && box.right > rail.left + 1);
      const margin = Number.parseFloat(getComputedStyle(list).paddingInlineEnd) || 0;
      const slack = fragment ? fragment.right - rail.left : 0;
      if (slack <= 0 || slack > margin) return;
      list.style.setProperty('--rail-end-slack', `${Math.ceil(slack)}px`);
      nav.scrollLeft = nav.scrollWidth - nav.clientWidth;
    }

    /* Tabbing along the same line: the browser will not do it for us. The line bleeds
       into the right margin (lab-section-header.css:167 — a negative margin-inline-end on
       the scroller, the margin given back as padding on the list) so the last word runs
       off the paper edge, and that half-word IS the scroll affordance the direction asks
       for. But the bled word sits in the list's own padding, so Chrome reads it as
       already in view and leaves the focus ring 20 px past the viewport at 360 (WCAG 2.2
       SC 2.4.11). scroll-padding-inline was measured on the scroller and changes nothing.

       Two obvious shortcuts are both wrong. scrollIntoView() moves the page as well as
       the row: the chrome is sticky and slides away on scroll-down, so even
       block: 'nearest' drags the document up by the height of the hidden bar (measured:
       64 px) at every width, including the ones where the line does not scroll. And a
       minimal nudge (scrollLeft += the overhang) is eaten by scroll-snap-type: x
       proximity, which snaps the row straight back to 0. So the word is aligned to the
       start of the rail, which is where the items' scroll-snap-align: start wants it
       anyway; the browser clamps the value at both ends. A no-op when the words fit —
       every width above 360 in Italian, fewer in French and German, where this earns its
       keep on wider viewports. */

    revealFocusedDestination(event) {
      const link = event.target.closest?.('.lab-line__nav a');
      const nav = link && this.querySelector('.lab-line__nav');
      if (!nav || nav.scrollWidth <= nav.clientWidth) return;
      const rail = nav.getBoundingClientRect();
      const word = link.getBoundingClientRect();
      if (word.right <= rail.right && word.left >= rail.left) return;
      nav.scrollLeft += word.left - rail.left;
    }
  }

  customElements.define('lab-header', LabHeader);
}
